import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Input, Typography, Space, Tag } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import DataCard from '../components/page/DataCard';
import { SearchOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import {
    DASHBOARD_SORTER_TOOLTIP,
    DASHBOARD_TABLE_STICKY,
    dashboardPagination,
    VULNERABILITY_MASTER_SCROLL_Y,
} from '../utils/adminDashboardTable';
import { sortByField } from '../utils/tableSorters';

const { Text } = Typography;

const col = (title, dataIndex, width, opts = {}) => ({
    title,
    dataIndex,
    key: dataIndex,
    width,
    /** Wrapping reads better than single-line ellipsis for long PLITS fields (wider columns + cell padding). */
    ellipsis: opts.ellipsis ?? false,
    sorter: sortByField(dataIndex),
    render: (v) => {
        const s = v == null || v === '' ? '-' : String(v);
        return (
            <span style={{ fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word' }}>
                {s}
            </span>
        );
    },
    ...opts,
});

/**
 * PLITS MAS Vulnerability Database — admin master data (platform_admin only API).
 */
const VulnerabilityMaster = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const instanceRef = useRef(instance);
    const accountRef = useRef(activeAccount);
    useEffect(() => { instanceRef.current = instance; }, [instance]);
    useEffect(() => { accountRef.current = activeAccount; }, [activeAccount]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    const getToken = useCallback(async () => {
        const tokenResponse = await instanceRef.current.acquireTokenSilent({
            ...apiTokenRequest,
            account: accountRef.current,
        });
        return tokenResponse.accessToken;
    }, []);

    const fetchRows = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/vulnerability-master`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (json.success) {
                setRows(json.data.rows || []);
            } else {
                setError(json.error?.message || 'Failed to load vulnerability database');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const filtered = useMemo(() => {
        if (!search.trim()) return rows;
        const q = search.toLowerCase();
        return rows.filter((r) =>
            [r.threatId, r.domain, r.impactedAsset, r.threat, r.vulnerability, r.scenario, r.impact,
                r.keyControls, r.detectionsTelemetry, r.source, r.tags]
                .some((f) => f && String(f).toLowerCase().includes(q)));
    }, [rows, search]);

    const vulnerabilityColumnsBase = useMemo(
        () => [
            col('Threat ID', 'threatId', 120),
            col('Domain', 'domain', 150),
            col('Impacted Asset', 'impactedAsset', 170),
            col('Threat', 'threat', 260),
            col('Vulnerability', 'vulnerability', 280),
            col('Scenario', 'scenario', 280),
            col('Impact', 'impact', 160),
            col('Key controls', 'keyControls', 300),
            col('Detections / telemetry', 'detectionsTelemetry', 300),
            col('Source', 'source', 180),
            {
                title: 'Tags',
                dataIndex: 'tags',
                key: 'tags',
                width: 160,
                sorter: sortByField('tags'),
                render: (v) => {
                    if (!v) return <Text type="secondary">-</Text>;
                    return <Tag style={{ fontSize: 12, lineHeight: 1.5, padding: '4px 10px' }}>{v}</Tag>;
                },
            },
        ],
        []
    );

    const { columns: vulnerabilityColumns, components: vulnerabilityTableComponents } = useResizableTableColumns(
        'vulnerability-master-v3',
        vulnerabilityColumnsBase
    );

    /** Match Control Taxonomy: numeric `scroll.x` = sum of column widths (not `max-content`) so columns stay tight. */
    const vulnerabilityTableScrollX = useMemo(() => {
        const sum = vulnerabilityColumns.reduce((acc, col) => {
            const w = col.width;
            return acc + (typeof w === 'number' ? w : 120);
        }, 0);
        return sum > 0 ? sum : 2200;
    }, [vulnerabilityColumns]);

    if (loading) {
        return <PageState status="loading" message="Loading vulnerability database..." />;
    }

    if (error) {
        return <PageState status="error" message={error} />;
    }

    return (
        <PageShell width="wide">
            <PageHeader
                title="Vulnerability Database - PLITS MAS"
                subtitle="Threat threads, vulnerabilities, and control hints (master data imported from CSV)"
            />

            <DataCard size="small">
                <Space wrap size="middle">
                    <Input
                        placeholder="Search threat ID, domain, tags, controls..."
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        style={{ width: 360 }}
                    />
                    <Text type="secondary">
                        {filtered.length} of {rows.length} rows
                    </Text>
                </Space>
            </DataCard>

            <Table
                className="policylens-data-table policylens-vulnerability-master-table"
                columns={vulnerabilityColumns}
                components={vulnerabilityTableComponents}
                dataSource={filtered}
                rowKey="id"
                size="middle"
                sticky={DASHBOARD_TABLE_STICKY}
                scroll={{ x: vulnerabilityTableScrollX, y: VULNERABILITY_MASTER_SCROLL_Y }}
                pagination={dashboardPagination({
                    pageSize: 25,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`,
                })}
                showSorterTooltip={DASHBOARD_SORTER_TOOLTIP}
                tableLayout="fixed"
                bordered
                style={{ fontSize: 13 }}
            />
        </PageShell>
    );
};

export default VulnerabilityMaster;
